import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { track } from '@/shared/analytics';

import {
  createMediaFolder,
  deleteMediaFolder,
  fileMediaIntoFolder,
  listMediaFolders,
  renameMediaFolder,
  type DeleteFolderResult,
  type MediaFolder,
  type MediaFolderTree,
} from './folders.api';
import { BRAND_MEDIA_QUERY_KEYS } from './queries';

export const mediaFoldersQueryOptions = () =>
  queryOptions({
    queryKey: BRAND_MEDIA_QUERY_KEYS.folders,
    queryFn: listMediaFolders,
  });

/**
 * Both mutations invalidate the whole brand-media prefix rather than only the tree.
 *
 * Filing changes a folder's count, which listing a folder answers, and which media a folder query
 * returns. Naming those separately is how one gets missed and a folder shows a count that no longer
 * matches what opening it produces.
 */
export function useCreateMediaFolder() {
  const queryClient = useQueryClient();

  return useMutation<MediaFolder, Error, Parameters<typeof createMediaFolder>[0]>({
    mutationFn: createMediaFolder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all }),
  });
}

/**
 * Files media, fonts, or a selection spanning both.
 *
 * Only the media prefix is invalidated here: a font is a sibling entity, and an entity may not
 * reach into another's cache. The host that owns both selections invalidates the fonts alongside
 * this.
 */
export function useFileMediaIntoFolder() {
  const queryClient = useQueryClient();

  return useMutation<
    { filed: number },
    Error,
    { mediaIds?: string[]; fontIds?: string[]; folderId: string | null }
  >({
    mutationFn: fileMediaIntoFolder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all }),
  });
}

export function useRenameMediaFolder() {
  const queryClient = useQueryClient();

  return useMutation<{ renamed: boolean }, Error, { folderId: string; name: string }>({
    mutationFn: renameMediaFolder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all }),
  });
}

/**
 * Deletes a folder and everything under it.
 *
 * The same broad prefix its siblings use, and for one more reason than they have: this changes the
 * tree, the listing, the facet counts the tag rail reads and the bound lookup a board resolves
 * through, all at once.
 *
 * No toast here. What to say depends on whether anything was filed in the folder, which is the
 * calling surface's to know -- the split `useDeleteBrandMedia` already leaves to its caller.
 */
export function useDeleteMediaFolder() {
  const queryClient = useQueryClient();

  return useMutation<DeleteFolderResult, Error, { folderId: string }>({
    mutationFn: deleteMediaFolder,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_MEDIA_QUERY_KEYS.all });
      track('brand_media_folder_deleted', {
        folder_count: result.deletedFolders,
        media_count: result.archivedMedia,
        font_count: result.archivedFonts,
      });
    },
  });
}

export type { DeleteFolderResult, MediaFolder, MediaFolderTree };
